import type { ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '@/features/auth/AuthContext';
import { ToastProvider } from '@/components/feedback/ToastProvider';
import { QueryProvider } from './QueryProvider';
import { WorkingContextProvider } from './WorkingContextProvider';
import { ThemeProvider } from './ThemeProvider';

/**
 * The provider stack, in dependency order.
 *
 * Router outermost so navigation is available everywhere; Theme next so the
 * first paint already carries the right palette; Query before Auth because the
 * auth context issues queries; Toast next so any screen can report an outcome;
 * WorkingContext innermost because it reads the campus and academic-year
 * pickers, which are permission-gated and therefore need Auth above them.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <BrowserRouter
      /*
       * Opt in to the v7 behaviours now, while the app is small. Both are
       * on by default in React Router v7, and adopting them at this size is a
       * no-op; discovering them during an upgrade later would not be.
       */
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <ThemeProvider>
        <QueryProvider>
          <ToastProvider>
            <AuthProvider>
              <WorkingContextProvider>{children}</WorkingContextProvider>
            </AuthProvider>
          </ToastProvider>
        </QueryProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
